// Running reorder list built up from AI Diagnose one-taps + manual adds.
// Each row deep-links to Grainger / Ferguson / Amazon / SupplyHouse
// searches so the office can order the next truckload in three clicks.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import { ShoppingCart, ArrowSquareOut, Trash, CheckCircle, Plus, PencilSimple, Briefcase, Check, X } from "@phosphor-icons/react";

export default function PartsReorderList() {
  const [data, setData] = useState({ open: [], ordered: [], open_count: 0 });
  const [addPart, setAddPart] = useState("");
  const [addQty, setAddQty] = useState(1);
  const [addJob, setAddJob] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingJob, setEditingJob] = useState(null); // { id, value }
  const [jobOptions, setJobOptions] = useState([]);

  const load = async () => {
    try { const r = await api.get("/parts-reorder"); setData(r.data); } catch {}
  };
  // Pull the tenant's HVAC units so we can suggest them as job refs (Carrier 25HCB6 / 1200 Sample St).
  const loadJobOptions = async () => {
    try {
      const r = await api.get("/hvac/units");
      const opts = (r.data?.units || []).map(u => {
        const label = u.nickname || u.equipment_id;
        return `${label} — ${u.site_address || ""}`.trim();
      });
      setJobOptions([...new Set(opts)]);  // de-dupe so <datalist> keys stay unique
    } catch { /* other verticals may not have HVAC units — silent */ }
  };
  useEffect(() => { load(); loadJobOptions(); }, []);

  // Prefill the "Job #" from the active HVAC job banner if one is running.
  const activeJob = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("hvac_active_unit") || "null");
      return u ? `${u.name} — ${u.address || ""}`.trim() : "";
    } catch { return ""; }
  }, []);
  useEffect(() => { if (activeJob && !addJob) setAddJob(activeJob); }, [activeJob]);

  const add = async (e) => {
    e?.preventDefault?.();
    if (!addPart.trim()) return;
    setLoading(true);
    try {
      await api.post("/parts-reorder", {
        part: addPart.trim(),
        qty: parseInt(addQty, 10) || 1,
        source: "manual",
        job_ref: addJob.trim() || null,
      });
      setAddPart(""); setAddQty(1);
      // keep the job ref in the field — tech usually adds several parts to the same job
      toast.success("Added to reorder list"); load();
    } catch (e) { toast.error("Add failed"); } finally { setLoading(false); }
  };

  const markOrdered = async (id) => {
    try { await api.post(`/parts-reorder/${id}/mark-ordered`); toast.success("Marked ordered"); load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove from reorder list?")) return;
    try { await api.delete(`/parts-reorder/${id}`); load(); } catch { toast.error("Delete failed"); }
  };
  const saveJobEdit = async () => {
    if (!editingJob) return;
    try {
      await api.patch(`/parts-reorder/${editingJob.id}`, { job_ref: editingJob.value.trim() || null });
      toast.success("Job # updated"); setEditingJob(null); load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6" data-testid="parts-reorder-page">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight inline-flex items-center gap-2">
              <ShoppingCart weight="fill" size={28} className="text-fuchsia-600"/> Truck Reorder List
            </h1>
            <div className="text-xs text-slate-500 mt-1">Built from AI Diagnose one-taps + manual adds. Attach a Job # to special orders so the office knows who it's for.</div>
          </div>
          <div className="px-3 py-1.5 rounded-lg bg-fuchsia-100 text-fuchsia-800 text-xs font-black uppercase tracking-widest">
            {data.open_count} open
          </div>
        </div>

        {/* Quick add */}
        <form onSubmit={add} className="mb-5 grid grid-cols-1 sm:grid-cols-[1fr_5.5rem_auto] gap-2" data-testid="reorder-add-form">
          <input value={addPart} onChange={e => setAddPart(e.target.value)}
            placeholder="Part name — dual-run cap 45/5 µF · SPP6 hard-start · IFC board"
            data-testid="reorder-add-part"
            className="h-11 px-3 border-2 border-slate-200 focus:border-fuchsia-500 outline-none rounded-lg font-medium"/>
          <input type="number" min={1} max={99} value={addQty} onChange={e => setAddQty(e.target.value)}
            data-testid="reorder-add-qty"
            className="h-11 px-3 border-2 border-slate-200 focus:border-fuchsia-500 outline-none rounded-lg font-medium text-center"/>
          <button type="submit" disabled={loading} data-testid="reorder-add-btn"
            className="h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-lg inline-flex items-center justify-center gap-1.5 shadow disabled:opacity-60">
            <Plus weight="bold" size={14}/> Add
          </button>
          <div className="sm:col-span-3 flex items-center gap-2">
            <Briefcase weight="fill" size={14} className="text-slate-400 flex-shrink-0"/>
            <input value={addJob} onChange={e => setAddJob(e.target.value)}
              list="reorder-job-options"
              placeholder="Job # / customer for special order (optional) — e.g. Roof unit A · 1200 Sample St"
              data-testid="reorder-add-job"
              className="flex-1 h-10 px-3 border-2 border-dashed border-slate-200 focus:border-fuchsia-500 outline-none rounded-lg text-sm"/>
            <datalist id="reorder-job-options">
              {jobOptions.map(j => <option key={j} value={j}/>)}
            </datalist>
          </div>
        </form>

        {data.open.length === 0 && (
          <div className="p-6 border-2 border-dashed border-slate-200 rounded-2xl text-center" data-testid="reorder-empty">
            <div className="text-slate-500">No open reorders yet. Tap <span className="font-black">+ Truck</span> on any AI Diagnose result to build the list.</div>
          </div>
        )}

        <div className="space-y-2">
          {data.open.map((it) => (
            <div key={it.id} data-testid={`reorder-row-${it.id}`}
              className="p-3 bg-white border border-slate-200 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{it.part}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">qty {it.qty}</span>
                  {it.source === "ai_diagnose" && <span className="text-[10px] font-black uppercase tracking-widest bg-fuchsia-100 text-fuchsia-700 px-1.5 py-0.5 rounded">AI Diagnose</span>}
                  {it.unit_ref && <span className="text-[10px] font-mono text-slate-500">from {it.unit_ref}</span>}
                </div>
                {/* Job # chip / inline editor */}
                {editingJob?.id === it.id ? (
                  <div className="mt-1.5 flex items-center gap-1.5" data-testid={`reorder-job-editor-${it.id}`}>
                    <Briefcase size={13} className="text-fuchsia-500"/>
                    <input value={editingJob.value}
                      onChange={e => setEditingJob({ ...editingJob, value: e.target.value })}
                      list="reorder-job-options"
                      autoFocus
                      onKeyDown={e => { if (e.key === "Enter") saveJobEdit(); if (e.key === "Escape") setEditingJob(null); }}
                      data-testid={`reorder-job-input-${it.id}`}
                      className="flex-1 h-7 px-2 border-2 border-fuchsia-300 focus:border-fuchsia-500 outline-none rounded text-xs"/>
                    <button type="button" onClick={saveJobEdit}
                      data-testid={`reorder-job-save-${it.id}`}
                      className="h-7 w-7 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center justify-center">
                      <Check weight="bold" size={12}/>
                    </button>
                    <button type="button" onClick={() => setEditingJob(null)}
                      className="h-7 w-7 rounded bg-slate-100 hover:bg-slate-200 text-slate-500 inline-flex items-center justify-center">
                      <X weight="bold" size={12}/>
                    </button>
                  </div>
                ) : it.job_ref ? (
                  <button type="button" onClick={() => setEditingJob({ id: it.id, value: it.job_ref })}
                    data-testid={`reorder-job-chip-${it.id}`}
                    className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-bold text-fuchsia-800 bg-fuchsia-50 hover:bg-fuchsia-100 border border-fuchsia-200 px-2 py-0.5 rounded-md">
                    <Briefcase weight="fill" size={11}/> {it.job_ref}
                    <PencilSimple size={10} className="opacity-60"/>
                  </button>
                ) : (
                  <button type="button" onClick={() => setEditingJob({ id: it.id, value: "" })}
                    data-testid={`reorder-job-add-${it.id}`}
                    className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-fuchsia-600 font-bold uppercase tracking-widest">
                    <Plus size={10}/> Attach job #
                  </button>
                )}
                {it.notes && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{it.notes}</div>}
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {[
                  ["Grainger", it.links.grainger, "bg-red-600"],
                  ["Supply House", it.links.supplyhouse, "bg-blue-600"],
                  ["Ferguson", it.links.ferguson, "bg-orange-600"],
                  ["Amazon", it.links.amazon, "bg-slate-900"],
                ].map(([label, href, color]) => (
                  <a key={label} href={href} target="_blank" rel="noreferrer"
                    className={`h-8 px-2 rounded-md ${color} hover:opacity-90 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1`}>
                    {label} <ArrowSquareOut weight="bold" size={10}/>
                  </a>
                ))}
                <button onClick={() => markOrdered(it.id)} data-testid={`reorder-ordered-${it.id}`}
                  className="h-8 px-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                  <CheckCircle weight="fill" size={10}/> Ordered
                </button>
                <button onClick={() => remove(it.id)} data-testid={`reorder-remove-${it.id}`}
                  className="h-8 w-8 rounded-md bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 inline-flex items-center justify-center">
                  <Trash size={13}/>
                </button>
              </div>
            </div>
          ))}
        </div>

        {data.ordered.length > 0 && (
          <div className="mt-8">
            <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Recently Ordered</div>
            <div className="space-y-1">
              {data.ordered.map(it => (
                <div key={it.id} className="p-2 text-xs text-slate-500 border border-slate-100 rounded-lg flex items-center gap-2">
                  <CheckCircle size={12} className="text-emerald-500"/>
                  <span className="font-mono">{it.part}</span>
                  {it.job_ref && <span className="text-[10px] text-fuchsia-700 bg-fuchsia-50 px-1.5 py-0.5 rounded font-bold">{it.job_ref}</span>}
                  <span className="ml-auto text-[10px]">{it.ordered_at?.slice(0,10)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
